import { InputHTMLAttributes, forwardRef, useState } from 'react';

type Tone = 'dark' | 'light';

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  tone?: Tone;
  type?: 'text' | 'email' | 'password' | 'number' | 'search' | 'tel' | 'url';
}

const toneClasses: Record<Tone, string> = {
  dark: 'bg-white border-neutral-20 text-neutral placeholder:text-neutral-30 focus:border-neutral-50',
  light: 'bg-white/10 border-white/10 text-white placeholder:text-white/30 focus:border-white/20',
};

const toggleToneClasses: Record<Tone, string> = {
  dark: 'text-neutral-30 hover:text-neutral-50 border-neutral-20 hover:border-neutral-50',
  light: 'text-white/40 hover:text-white/60 border-white/10 hover:border-white/20',
};

const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
);

const EyeSlashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12c1.292 4.338 5.31 7.5 10.066 7.5.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
  </svg>
);

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ tone = 'dark', type = 'text', className = '', ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';

    const classes = [
      'w-full h-9 px-3 text-[14px] border rounded-[10px] focus:outline-none transition-colors',
      toneClasses[tone],
      isPassword ? 'pr-12' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const input = (
      <input
        ref={ref}
        className={classes}
        type={isPassword && showPassword ? 'text' : type}
        {...props}
      />
    );

    if (!isPassword) return input;

    return (
      <div className="relative">
        {input}
        <button
          type="button"
          className={`absolute right-1 top-1/2 -translate-y-1/2 flex items-center justify-center h-8 w-8 -mr-0.5 rounded-md border transition-colors ${toggleToneClasses[tone]}`}
          onClick={() => setShowPassword((prev) => !prev)}
        >
          {showPassword ? <EyeIcon /> : <EyeSlashIcon />}
        </button>
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
